import { useTranslation } from 'react-i18next';
import { Check, Clock, X, MinusCircle } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Badge } from '@/shared/components/ui/badge';
import { formatDisplayDate } from '@/shared/utils/dateUtils';
import type { TimeOffApprovalStepDto } from '../../types';

interface RequestStepperProps {
  steps: TimeOffApprovalStepDto[];
}

const STEP_ICON: Record<string, typeof Check> = {
  Approved: Check,
  Rejected: X,
  Skipped: MinusCircle,
  Pending: Clock,
  Waiting: Clock,
};

const STEP_COLOR: Record<string, string> = {
  Approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
  Rejected: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
  Skipped: 'bg-muted text-muted-foreground',
  Pending: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
  Waiting: 'bg-muted text-muted-foreground',
};

/**
 * Timeline of a request's approval chain. Resolved steps show their outcome,
 * the current `Pending` step is highlighted with its `deadlineAt`, and
 * `Waiting` (not-yet-active) steps show no deadline — the backend only
 * populates it once a step activates.
 */
export function RequestStepper({ steps }: RequestStepperProps) {
  const { t } = useTranslation('timeOff');
  const sorted = [...steps].sort((a, b) => a.order - b.order);

  return (
    <ol className="space-y-3" data-testid="time-off-request-stepper">
      {sorted.map((step) => {
        const status = step.status ?? 'Waiting';
        const Icon = STEP_ICON[status] ?? Clock;
        const isPending = status === 'Pending';

        return (
          <li
            key={step.id ?? step.order}
            className={cn(
              'flex items-start gap-3 rounded-md border border-border p-3',
              isPending && 'border-primary/40 bg-primary/5',
            )}
            data-testid={`time-off-request-step-${step.order}`}
          >
            <span
              className={cn(
                'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full',
                STEP_COLOR[status] ?? STEP_COLOR.Waiting,
              )}
              aria-hidden="true"
            >
              <Icon className="size-3.5" />
            </span>
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{t(`requests.stepper.${status.toLowerCase()}`)}</Badge>
                {step.source && (
                  <span className="text-xs text-muted-foreground">{step.source}</span>
                )}
              </div>
              {step.deadlineAt && (
                <p className="text-xs text-muted-foreground">
                  {t('requests.stepper.deadline', { date: formatDisplayDate(step.deadlineAt) })}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
